import { isEmptyString } from '@/utils/string';

/**
 * The option list an attribute-name picker shows, from the names it holds and
 * the term the reader has typed.
 *
 * Shared by every attribute-name picker because the rule is the picker's
 * arrangement rather than any one catalogue's: the row controls the select's
 * search input, which turns Pluma's own client-side filter off, so narrowing
 * the pool is the caller's job wherever the names came from.
 *
 * Three parts, in this order for reasons each picker depends on:
 *
 * - the pool is narrowed by case-insensitive substring on the typed term;
 * - the named attribute is appended *after* narrowing, so a saved condition
 *   keeps its label rather than going blank while a non-matching term is typed;
 * - a term nothing matches exactly leads the list as its own option, so any
 *   attribute name can be specified rather than only picked.
 *
 * @param pools The name sources to offer, merged in the order given.
 * @param searchTerm What the reader has typed, as the select's input holds it.
 * @param selectedField The name the condition currently holds, or '' when fresh.
 */
export function attributeNameOptions(
  pools: readonly (readonly string[])[],
  searchTerm: string,
  selectedField: string,
): { value: string; label: string }[] {
  const needle = searchTerm.trim().toLowerCase();
  const names = new Set(pools.flat().filter((name) => name.toLowerCase().includes(needle)));
  const custom = !isEmptyString(searchTerm) && !names.has(searchTerm) ? [searchTerm] : [];
  if (!isEmptyString(selectedField)) {
    names.add(selectedField);
  }
  return [...custom, ...names].map((name) => ({ value: name, label: name }));
}
